import { useState, useEffect, useCallback } from 'react';
import { Gauge, ChevronLeft, ChevronRight, Download, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import * as XLSX from 'xlsx';

const KilometriqueTracking = ({ user, activeModule = 'b2b' }) => {
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(() => {
    try {
      return localStorage.getItem('km_selected_site_id') || 'all';
    } catch {
      return 'all';
    }
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const getDateRange = useCallback(() => {
    const d = new Date(currentDate);
    let start, end;
    if (viewMode === 'day') {
      const ds = d.toISOString().split('T')[0];
      return { start_date: ds, end_date: ds };
    } else if (viewMode === 'week') {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(d); start.setDate(diff);
      end = new Date(start); end.setDate(start.getDate() + 6);
    } else {
      start = new Date(d.getFullYear(), d.getMonth(), 1);
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    }
    return { start_date: start.toISOString().split('T')[0], end_date: end.toISOString().split('T')[0] };
  }, [viewMode, currentDate]);

  const getDateLabel = () => {
    const d = new Date(currentDate);
    if (viewMode === 'day') return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (viewMode === 'week') {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const start = new Date(d); start.setDate(diff);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') d.setDate(d.getDate() + dir);
    else if (viewMode === 'week') d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
    setPage(1);
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const params = { start_date, end_date, page, page_size: 50 };
      if (selectedSiteId !== 'all') {
        params.site_id = selectedSiteId;
      }
      const res = await axios.get(`${API}/kilometrique/logs`, {
        params,
        withCredentials: true
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (err) {
      console.error('Failed to fetch km logs:', err);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, page, selectedSiteId]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const res = await axios.get(`${API}/kilometrique/stats`, {
        params: { start_date, end_date },
        withCredentials: true
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch km stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const res = await axios.get(`${API}/sites`, { withCredentials: true });
        setSites(res.data || []);
      } catch (err) {
        console.error('Failed to fetch sites:', err);
      }
    };
    fetchSites();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('km_selected_site_id', selectedSiteId);
    } catch {
      // ignore storage errors
    }
  }, [selectedSiteId]);

  useEffect(() => {
    if (selectedSiteId !== 'all' && sites.length > 0 && !sites.some(s => s.site_id === selectedSiteId)) {
      setSelectedSiteId('all');
    }
  }, [sites, selectedSiteId]);

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  const exportExcel = () => {
    if (logs.length === 0) return;
    const rows = logs.map(l => ({
      'Date': l.date, 'Site': l.site_name || '', 'Chauffeur': l.user_name || '',
      'Véhicule': l.vehicle_plate || '', 'Km Début': l.km_debut, 'Km Fin': l.km_fin,
      'Distance (km)': l.distance
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Kilométrique');
    if (stats?.by_site?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        stats.by_site.map(s => ({ 'Site': s.label, 'Missions': s.count, 'Distance (km)': s.distance }))
      ), 'Par Site');
    }
    if (stats?.by_driver?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        stats.by_driver.map(s => ({ 'Chauffeur': s.label, 'Missions': s.count, 'Distance (km)': s.distance }))
      ), 'Par Chauffeur');
    }
    if (stats?.by_vehicle?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        stats.by_vehicle.map(s => ({ 'Véhicule': s.label, 'Missions': s.count, 'Distance (km)': s.distance }))
      ), 'Par Véhicule');
    }
    const { start_date, end_date } = getDateRange();
    XLSX.writeFile(wb, `Kilometrique_${start_date}_${end_date}.xlsx`);
  };

  const totalDistance = logs.reduce((s, l) => s + (l.distance || 0), 0);

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6" data-testid="km-tracking-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="km-tracking-title">Suivi Kilométrique</h1>
            <p className="text-sm text-gray-500 mt-1">Distances parcourues par mission</p>
          </div>
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={logs.length === 0} data-testid="km-export-btn">
            <Download className="w-4 h-4 mr-1" /> Export Excel
          </Button>
        </div>

        {/* Date nav */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex gap-1">
                {['day', 'week', 'month'].map(m => (
                  <Button key={m} variant={viewMode === m ? 'default' : 'ghost'} size="sm" onClick={() => { setViewMode(m); setPage(1); }} data-testid={`km-view-${m}`}>
                    {m === 'day' ? 'Jour' : m === 'week' ? 'Semaine' : 'Mois'}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="km-nav-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm font-medium min-w-[200px] text-center capitalize" data-testid="km-date-label">{getDateLabel()}</span>
                <Button variant="ghost" size="sm" onClick={() => navigate(1)} data-testid="km-nav-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setCurrentDate(new Date()); setPage(1); }}>Aujourd'hui</Button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <label htmlFor="km-site-filter" className="text-sm text-gray-600">Site :</label>
              <select
                id="km-site-filter"
                value={selectedSiteId}
                onChange={(e) => { setSelectedSiteId(e.target.value); setPage(1); }}
                className="text-sm border rounded-md px-2 py-1 bg-white"
                data-testid="km-site-filter"
              >
                <option value="all">Tous les sites</option>
                {sites.map(site => (
                  <option key={site.site_id} value={site.site_id}>{site.name}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {[{ id: 'logs', label: 'Historique' }, { id: 'stats', label: 'Statistiques' }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id ? 'border-slate-800 text-slate-800' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`} data-testid={`km-tab-${tab.id}`}>{tab.label}</button>
          ))}
        </div>

        {/* Logs tab */}
        {activeTab === 'logs' && (
          <div>
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-800" data-testid="km-kpi-count">{total}</p>
                <p className="text-xs text-gray-500">Missions avec km</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600" data-testid="km-kpi-distance">{totalDistance.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Km parcourus</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600" data-testid="km-kpi-avg">{total > 0 ? Math.round(totalDistance / total) : 0}</p>
                <p className="text-xs text-gray-500">Km moy. / mission</p>
              </CardContent></Card>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div></div>
            ) : logs.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500">
                <Gauge className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune mission avec kilométrage pour cette période</p>
              </CardContent></Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="km-logs-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 font-medium text-gray-600">Site</th>
                      <th className="text-left p-3 font-medium text-gray-600">Chauffeur</th>
                      <th className="text-left p-3 font-medium text-gray-600">Véhicule</th>
                      <th className="text-right p-3 font-medium text-gray-600">Km Début</th>
                      <th className="text-right p-3 font-medium text-gray-600">Km Fin</th>
                      <th className="text-right p-3 font-medium text-gray-600">Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((l, i) => (
                      <tr key={l.schedule_id || i} className="border-b hover:bg-gray-50" data-testid={`km-row-${i}`}>
                        <td className="p-3">{l.date}</td>
                        <td className="p-3 text-gray-600">{l.site_name || '-'}</td>
                        <td className="p-3">{l.user_name || '-'}</td>
                        <td className="p-3 font-mono text-xs">{l.vehicle_plate || '-'}</td>
                        <td className="p-3 text-right text-gray-500">{l.km_debut?.toLocaleString()}</td>
                        <td className="p-3 text-right text-gray-500">{l.km_fin?.toLocaleString()}</td>
                        <td className="p-3 text-right font-semibold text-green-700">{l.distance?.toLocaleString()} km</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">Page {page} / {totalPages} ({total} résultats)</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="km-page-prev"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="km-page-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {statsLoading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div></div>
            ) : !stats || (!stats.by_site?.length && !stats.by_driver?.length && !stats.by_vehicle?.length) ? (
              <Card><CardContent className="p-8 text-center text-gray-500">
                <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune donnée pour cette période</p>
              </CardContent></Card>
            ) : (
              <>
                {stats.total_distance > 0 && (
                  <Card className="border-slate-200 bg-slate-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-gray-500 mb-1">Distance totale parcourue</p>
                      <p className="text-3xl font-bold text-slate-800" data-testid="km-total-distance">
                        {stats.total_distance.toLocaleString()} <span className="text-lg font-normal text-gray-500">km</span>
                      </p>
                      <p className="text-sm text-gray-400">{stats.total_missions} missions</p>
                    </CardContent>
                  </Card>
                )}
                {stats.by_site?.length > 0 && <KmStatsTable title="Par Site" data={stats.by_site} testId="km-stats-site" />}
                {stats.by_driver?.length > 0 && <KmStatsTable title="Par Chauffeur" data={stats.by_driver} testId="km-stats-driver" />}
                {stats.by_vehicle?.length > 0 && <KmStatsTable title="Par Véhicule" data={stats.by_vehicle} testId="km-stats-vehicle" />}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

const KmStatsTable = ({ title, data, testId }) => (
  <Card>
    <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-2 font-medium text-gray-600">{title.replace('Par ', '')}</th>
              <th className="text-right p-2 font-medium text-gray-600">Missions</th>
              <th className="text-right p-2 font-medium text-gray-600">Distance (km)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className="border-b hover:bg-gray-50">
                <td className="p-2 font-medium">{row.label}</td>
                <td className="p-2 text-right">{row.count}</td>
                <td className="p-2 text-right font-semibold text-green-700">{row.distance?.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default KilometriqueTracking;
